// ===========================================
// Two-Factor Setup Dialog Component
// ===========================================
// Multi-step dialog: QR code → Verify → Backup Codes

import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useInitiate2FASetup, useVerify2FASetup } from '../../../hooks/useTwoFactor';
import BackupCodesDisplay from './BackupCodesDisplay';

interface Props {
  open: boolean;
  onClose: () => void;
}

type SetupStep = 'qr' | 'verify' | 'backup';

const STEPS = ['Scan QR Code', 'Verify Code', 'Save Backup Codes'];

function getActiveStepIndex(step: SetupStep): number {
  switch (step) {
    case 'qr': return 0;
    case 'verify': return 1;
    case 'backup': return 2;
  }
}

export default function TwoFactorSetupDialog({ open, onClose }: Props) {
  const [step, setStep] = useState<SetupStep>('qr');
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [secretCopied, setSecretCopied] = useState(false);

  const initSetup = useInitiate2FASetup();
  const verifySetup = useVerify2FASetup();

  // Trigger setup on dialog open
  const handleEntered = () => {
    initSetup.mutateAsync();
  };

  const handleCopySecret = async () => {
    if (initSetup.data?.data?.secret) {
      try {
        await navigator.clipboard.writeText(initSetup.data.data.secret);
        setSecretCopied(true);
        setTimeout(() => setSecretCopied(false), 2000);
      } catch {
        // ignore clipboard errors
      }
    }
  };

  const handleVerify = async () => {
    try {
      const result = await verifySetup.mutateAsync({ code: verifyCode });
      if (result?.data?.codes) {
        setBackupCodes(result.data.codes);
        setStep('backup');
      }
    } catch {
      // Error handled by hook's onError
    }
  };

  const handleClose = () => {
    // Reset all state
    setStep('qr');
    setVerifyCode('');
    setBackupCodes([]);
    setSecretCopied(false);
    initSetup.reset();
    verifySetup.reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={step === 'backup' ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEntered: handleEntered }}
    >
      <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Stepper activeStep={getActiveStepIndex(step)} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Step 1: QR Code */}
        {step === 'qr' && (
          <Box>
            {initSetup.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : initSetup.data?.data ? (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <Box
                    component="img"
                    src={initSetup.data.data.qrCodeUrl}
                    alt="2FA QR Code"
                    sx={{
                      width: 200,
                      height: 200,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Can't scan? Enter this code manually:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={initSetup.data.data.secret}
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={secretCopied ? 'Copied!' : 'Copy secret'}>
                          <IconButton size="small" onClick={handleCopySecret}>
                            {secretCopied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 1 }}
                />
              </>
            ) : null}
          </Box>
        )}

        {/* Step 2: Verify */}
        {step === 'verify' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the 6-digit code from your authenticator app to verify setup.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Verification Code"
              value={verifyCode}
              onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                style: { fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: 8, textAlign: 'center' },
              }}
              error={verifySetup.isError}
              helperText={verifySetup.isError ? 'Invalid code. Please try again.' : ''}
            />
          </Box>
        )}

        {/* Step 3: Backup Codes */}
        {step === 'backup' && (
          <Box>
            <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
              Two-factor authentication is now enabled!
            </Typography>
            <BackupCodesDisplay codes={backupCodes} />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'qr' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => setStep('verify')}
              disabled={initSetup.isPending || !initSetup.data?.data}
            >
              Next
            </Button>
          </>
        )}
        {step === 'verify' && (
          <>
            <Button onClick={() => { setVerifyCode(''); verifySetup.reset(); setStep('qr'); }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleVerify}
              disabled={verifyCode.length !== 6 || verifySetup.isPending}
            >
              {verifySetup.isPending ? 'Verifying...' : 'Verify'}
            </Button>
          </>
        )}
        {step === 'backup' && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
